import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { aggregateBills, type Stop } from '@/lib/grouping';

interface ShipmentInfoCardProps {
  stop: Stop;
}

/**
 * Context card shown during the en-route/arrived phases so the driver always
 * sees who/what/where without leaving the workflow. Mirrors the native
 * ShipmentInfoCard: company, address, bill count, aggregated pcs/weight, refs
 * and the per-bill tags.
 */
export function ShipmentInfoCard({ stop }: ShipmentInfoCardProps) {
  const { pcs, wgt, refs } = aggregateBills(stop.bills);
  const accent = stop.kind === 'pickup' ? 'pickup' : 'delivery';
  const dirLabel = stop.kind === 'pickup' ? 'From' : 'To';
  const tags = stop.bills.map((b) => b.remF02Tag).filter(Boolean).join(', ');

  return (
    <Card accent={accent}>
      <CardHeader>
        <CardTitle>{stop.name || (stop.kind === 'pickup' ? 'Shipper' : 'Consignee')}</CardTitle>
        <span className="text-sm font-bold text-brand-muted">
          {stop.bills.length} bill{stop.bills.length === 1 ? '' : 's'}
        </span>
      </CardHeader>
      <CardBody>
        <div className="text-sm">
          <strong>{dirLabel}:</strong> {stop.address || '--'}
          {stop.city ? `, ${stop.city}` : ''}
        </div>
        <div className="mt-1 text-xs text-brand-muted">
          {pcs} pcs &middot; {wgt} lbs
        </div>
        {refs && <div className="mt-1 truncate text-xs text-brand-muted">Ref: {refs}</div>}
        {tags && <div className="mt-1 text-xs text-brand-muted">{tags}</div>}
      </CardBody>
    </Card>
  );
}
